/** @jsxImportSource @emotion/react */
import {
  EuiButtonIcon,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPopover,
  htmlIdGenerator,
} from "@inscopix/ideas-eui";
import { Group, IdeasFile } from "@inscopix/ideas-hyperformula";
import { useState } from "react";
import { CellValueRendererBase } from "./CellValueRendererBase";
import { CellValueRendererFile } from "./CellValueRendererFile";
import { CellAddress } from "../store/DataTableProvider.types";
import { useResizeDetector } from "react-resize-detector";
import { getNumBadgesFit } from "styles/useFileGridRendererStyles/getNumBadgesFit";
import { BadgePlain } from "../plain/BadgePlain";
import { css } from "@emotion/react";

const isIdeasFile = <T,>(value: T | IdeasFile): value is IdeasFile => {
  return value instanceof IdeasFile;
};

interface CellValueRendererGroupProps {
  address?: CellAddress;
  group: Group;
  isInGroup?: boolean;
  isDraggable?: boolean;
}

/** Component that renders a `Group` cell value` */
export const CellValueRendererGroup = ({
  address,
  group,
  isInGroup,
  isDraggable = false,
}: CellValueRendererGroupProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { width, ref } = useResizeDetector();

  const values = group.cellValues.filter((val) => val !== null && val !== "");
  const hasFile = values.some(isIdeasFile);

  if (values.length === 0) {
    return null;
  }

  if (values.length === 1) {
    const value = values[0];
    return <CellValueRendererBase value={value} />;
  }

  // Calculate the number of badges that fit in the cell
  const numBadgesFit = getNumBadgesFit(width);

  const isOverflow = numBadgesFit < values.length;
  const visibleFiles = isOverflow ? values.slice(0, numBadgesFit - 1) : values;

  return (
    <EuiFlexGroup
      alignItems="center"
      gutterSize="xs"
      responsive={false}
      ref={ref}
      css={css`
        width: 100%;
        .see-all-files-button {
          visibility: hidden;
        }
        &:hover .see-all-files-button {
          visibility: visible;
        }
      `}
    >
      {visibleFiles.map((cellValue, idx, arr) => {
        const isLastValue = idx === arr.length - 1;
        return (
          <EuiFlexItem grow={false} key={htmlIdGenerator()()}>
            <CellValueRendererBase
              isInGroup
              isLastValueInGroup={isLastValue}
              value={cellValue}
            />
          </EuiFlexItem>
        );
      })}

      {isOverflow && (
        <BadgePlain color={"#D3DAE6"}>
          +{values.length - visibleFiles.length}
        </BadgePlain>
      )}
      {/* Show inspect button for groups containing files */}
      {hasFile && !isInGroup && (
        <EuiFlexItem
          grow={false}
          css={css`
            position: absolute;
            right: 0;
          `}
        >
          <EuiPopover
            anchorPosition="downCenter"
            isOpen={isPopoverOpen}
            panelPaddingSize="s"
            closePopover={() => setIsPopoverOpen(false)}
            display="block"
            repositionOnScroll
            button={
              <EuiButtonIcon
                className="see-all-files-button"
                aria-label="See all files"
                iconType="inspect"
                onClick={() => setIsPopoverOpen((isOpen) => !isOpen)}
              />
            }
          >
            <EuiFlexGroup direction="column" gutterSize="s" responsive={false}>
              {values.filter(isIdeasFile).map((file) => (
                <CellValueRendererFile
                  key={file.attrs.id}
                  fileId={file.attrs.id}
                  address={address}
                  isDraggable={isDraggable}
                />
              ))}
            </EuiFlexGroup>
          </EuiPopover>
        </EuiFlexItem>
      )}
    </EuiFlexGroup>
  );
};
